<template>
  <canvas
    id="c"
    ref="analyticCanvasRef"
    class="analytics-canvas"
  />
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';

import { fabric } from 'fabric';
import { debounce } from 'lodash';

import {
  IntrusionConfiguration,
  LoiteringConfiguration,
  MotionConfiguration,
  AnalyticsSettingsGet,
  Lines,
} from '@eencloud/eewc-components/src/service/api-types';

import vuetify from '@/plugins/vuetify';

import { ANALYTIC_EVENTS } from '@/constants';

type AnalyticSettingsConfiguration = LoiteringConfiguration | IntrusionConfiguration | MotionConfiguration;
type Canvas = InstanceType<typeof fabric.Canvas>;
type Coordinate = { x: number; y: number };
type RegionCoordinates = Coordinate[];
interface ImageDimensions {
  width: number;
  height: number;
}
interface OverlayDimensions extends ImageDimensions {
  offsetX: number;
  offsetY: number;
}
type EventCoordinatesCollection = (Coordinate[] | undefined)[];

const props = defineProps<{
  cameraId: string;
  container: HTMLElement;
  imageDimensions?: ImageDimensions;
  analyticSettingsData: AnalyticsSettingsGet[] | undefined;
  showMotionRoi: boolean | undefined;
  showMotionRoiAndLines: boolean | undefined;
}>();

const ROI_COLORS = {
  BLUE: vuetify.framework.theme.themes.light.accent,
  GREEN: vuetify.framework.theme.themes.light.analytics,
};
const CANVAS_CONFIG = {
  STROKE_WIDTH: 2,
  COLORS: ROI_COLORS,
  DEBOUNCE_DELAY: 100,
};

let containerResizeObserver: ResizeObserver;

const analyticCanvasRef = ref<HTMLCanvasElement | null>(null);
const analyticCanvas = ref<Canvas | null>(null);
const analyticOverlayDimensions = ref<OverlayDimensions>({
  width: 0,
  height: 0,
  offsetX: 0,
  offsetY: 0,
});

const debouncedDrawRoiRegions = debounce(drawRoiRegions, CANVAS_CONFIG.DEBOUNCE_DELAY);
const debouncedInitialization = debounce(initialize, CANVAS_CONFIG.DEBOUNCE_DELAY);

onMounted(() => {
  debouncedInitialization();
});

onBeforeUnmount(() => {
  if (containerResizeObserver) {
    containerResizeObserver.unobserve(props.container);
    containerResizeObserver.disconnect();
  }
  disposeAnalyticCanvas();
});

// render the canvas only once if not resizing the window
watch(
  () => [props.analyticSettingsData, props.imageDimensions],
  (newValue) => {
    if (newValue[0] && newValue[1]) {
      debouncedInitialization();
    }
  }
);

function getContainerDimensions() {
  return {
    width: props.container.clientWidth,
    height: props.container.clientHeight,
  };
}

function getRoiRegionCoordinates(eventConfig: AnalyticSettingsConfiguration, eventType: string) {
  if (!props.analyticSettingsData) return [];

  if (eventConfig) {
    const currentEventConfig =
      eventType === ANALYTIC_EVENTS.MOTION_DETECTION ? filterMotionDetectionEvent(eventConfig) : eventConfig;

    const regionCoordinates = currentEventConfig?.regions?.map((region) => {
      const regionCoordinate = region.polygon?.map((polygonCoordinate) =>
        getScaledCoordinate(polygonCoordinate[0], polygonCoordinate[1])
      );

      return regionCoordinate;
    });

    return regionCoordinates;
  }

  return [];
}

function filterMotionDetectionEvent(motionEventConfig: AnalyticSettingsConfiguration) {
  motionEventConfig.regions = motionEventConfig.regions?.filter((region) => {
    const eventName =
      region.name && ['person detection', 'vehicle detection'].includes(region.name.split(':')[0].toLowerCase());

    return !eventName;
  });

  return motionEventConfig;
}

function getLineCoordinates(lines: Lines) {
  const lineCoordinates = lines.map((line) =>
    line.coordinates.map((coordinate) => getScaledCoordinate(coordinate[0], coordinate[1]))
  );

  return lineCoordinates;
}

function drawRoiPolygons(eventCoordinatesCollection: EventCoordinatesCollection, eventType: string) {
  eventCoordinatesCollection.forEach((coordinates) => {
    const color =
      eventType === ANALYTIC_EVENTS.MOTION_DETECTION ? CANVAS_CONFIG.COLORS.BLUE : CANVAS_CONFIG.COLORS.GREEN;
    return coordinates && addPolygon(coordinates, color);
  });
}

async function initialize() {
  containerResizeObserver = new ResizeObserver(debouncedDrawRoiRegions);
  containerResizeObserver.observe(props.container);
}

function getImageDimensions() {
  if (!props.imageDimensions) return null;

  return {
    width: props.imageDimensions?.width,
    height: props.imageDimensions?.height,
  };
}

function drawRoiRegions() {
  nextTick(() => {
    disposeAnalyticCanvas();

    const canvasEl = analyticCanvasRef.value;
    const containerDimensions = getContainerDimensions();
    const imageDimensions = getImageDimensions();

    if (!canvasEl || !containerDimensions) return;

    imageDimensions &&
      adjustSize(containerDimensions.width, containerDimensions.height, imageDimensions.width / imageDimensions.height);
    const { width, offsetX, offsetY } = analyticOverlayDimensions.value;

    try {
      analyticCanvas.value = new fabric.Canvas(canvasEl, {
        width: props.imageDimensions ? width : props.container.clientWidth,
        height: props.container.clientHeight,
      });
    } catch (error) {
      console.error('Failed to initialize analytics canvas', error);
    }

    if (analyticCanvas.value) {
      setAnalyticCanvasPosition(offsetX, offsetY);
      analyticCanvas.value.selection = false;
      analyticCanvas.value.skipTargetFind = true;
      drawPolygons();
    }
  });
}

function setAnalyticCanvasPosition(offsetX: number, offsetY: number) {
  if (!analyticCanvasRef.value) return;

  analyticCanvasRef.value.style.left = offsetX + 'px';
  analyticCanvasRef.value.style.top = offsetY + 'px';
}

function drawPolygons() {
  if (!analyticCanvas.value || !props.analyticSettingsData) return;

  props.analyticSettingsData.forEach((event) => {
    if (event.configuration && event.type && Object.values(ANALYTIC_EVENTS).includes(event.type)) {
      const isMotionDetectionEvent = event.type === ANALYTIC_EVENTS.MOTION_DETECTION;
      const isAllowedMotionDetectionEvent = isMotionDetectionEvent && props?.showMotionRoi;
      const isAllowedAnalyticEvent = !isMotionDetectionEvent && props?.showMotionRoiAndLines;

      if (isAllowedMotionDetectionEvent || isAllowedAnalyticEvent) {
        const regionsCoordinates =
          event.type === ANALYTIC_EVENTS.OBJECT_LINE_CROSS && 'lines' in event.configuration
            ? getLineCoordinates(event.configuration.lines)
            : getRoiRegionCoordinates(event.configuration, event.type);

        regionsCoordinates && drawRoiPolygons(regionsCoordinates, event.type);
      }
    }
  });
}

function addPolygon(regionCoordinates: RegionCoordinates, color: string) {
  const currentPolygon = new fabric.Polygon(regionCoordinates, {
    fill: 'transparent',
    stroke: color,
    strokeWidth: CANVAS_CONFIG.STROKE_WIDTH,
  });

  if (analyticCanvas.value) analyticCanvas.value.add(currentPolygon);
}

function getScaledCoordinate(x: number, y: number): Coordinate {
  const canvas = analyticCanvasRef.value ?? props.container;

  const { width, height } = canvas.getBoundingClientRect();

  return {
    x: x * width,
    y: y * height,
  };
}

function adjustSize(containerWidth: number, containerHeight: number, aspectRatio: number) {
  let displayedWidth, displayedHeight, offsetX, offsetY;
  const containerAspectRatio = containerWidth / containerHeight;
  // Calculate the scaled dimensions based on aspect ratios
  if (containerAspectRatio > aspectRatio) {
    // Container is wider than the content (poster or video)
    displayedHeight = containerHeight; // match height
    displayedWidth = displayedHeight * aspectRatio; // scale width
    offsetX = (containerWidth - displayedWidth) / 2; // center horizontally
    offsetY = 0; // no vertical offset
  } else {
    // Container is taller than the content
    displayedWidth = containerWidth; // match width
    displayedHeight = displayedWidth / aspectRatio; // scale height
    offsetX = 0; // no horizontal offset
    offsetY = (containerHeight - displayedHeight) / 2; // center vertically
  }
  if (!analyticCanvasRef.value) return;
  // Update the overlay to match the content size and position
  analyticOverlayDimensions.value = { width: displayedWidth, height: displayedHeight, offsetX, offsetY };
}

function disposeAnalyticCanvas() {
  if (!analyticCanvas.value) return;

  analyticCanvas.value.dispose();
  analyticCanvas.value = null;
}
</script>

<style lang="scss" scoped>
.analytics-canvas {
  top: 0 !important;
  height: 100% !important;
  width: 100% !important;
  cursor: pointer !important;
}
</style>
